import {setGraneroTarget} from '@apps-common/geocorn-sdk/api';

// Types ------------------------------------------------------------------

type URL = string;

type RegionlessConfig = {
  basemaps?: URL;
  basemapTiles?: URL;
  basemapTilesDomainTemplate?: URL;
  blobz?: URL;
  data?: URL;
  destinations?: URL;
  explorerUtils?: URL;
  features?: URL;
  orders?: URL;
  pdaAdmin?: URL;
  sceneTiles?: URL;
  sceneTilesDomainTemplate?: URL;
  subscriptions?: URL;
};

type RegionConfig = {
  regions: {
    [key: string]: URL;
  };
  accessControl?: URL;
  catalog?: URL;
  configuration?: URL;
  dataCollections?: URL;
  globalDataCollections?: URL;
  ims?: URL;
  process?: URL;
  shServiceLocations?: URL;
};

type BaseConfig = {base?: string};

type APIConfig = BaseConfig & RegionlessConfig & RegionConfig;

// Regions ----------------------------------------------------------------

export const AWS_EU_CENTRAL_1 = 'aws-eu-central-1';
export const AWS_US_WEST_2 = 'aws-us-west-2';
export const GCP_US_CENTRAL_1 = 'gcp-us-central1';

// Base -------------------------------------------------------------------

const env = import.meta.env;

const isStagingAuth = env.VITE_AUTH_TARGET === 'staging';

const API_URLS: APIConfig = {
  base:
    env.VITE_API_BASE_URL ??
    (isStagingAuth
      ? 'https://api.staging.planet-labs.com'
      : 'https://api.planet.com'),
  regions: {
    [AWS_EU_CENTRAL_1]:
      env.VITE_AWS_EU_CENTRAL_1 ?? 'https://api.eu-1.planet.com',
    [AWS_US_WEST_2]: env.VITE_AWS_US_WEST_2 ?? 'https://api.us-2.planet.com',
    [GCP_US_CENTRAL_1]:
      env.VITE_GCP_US_CENTRAL_1 ?? 'https://api.us-1.planet.com',
  },
};

// Regionless APIs --------------------------------------------------------

API_URLS.basemaps = env.VITE_BASEMAPS_API_BASE ?? '/basemaps/v1';

// sceneTiles and basemapTiles configs look the same in prod, but internally they are 2 separate services (webtiles & yats)
// this allows flexibility if we need to test with internal routes
API_URLS.basemapTiles =
  env.VITE_BASEMAPS_TILES_BASE_URL ?? 'https://tiles.planet.com/basemaps/v1';

API_URLS.basemapTilesDomainTemplate =
  env.VITE_BASEMAPS_TILES_DOMAIN_TEMPLATE ??
  'https://tiles{0-3}.planet.com/basemaps/v1';

API_URLS.blobz = env.VITE_BLOBZ_API_URL ?? '/apps/v0/b';

API_URLS.data = env.VITE_DATA_API_URL ?? '/data/v1';

API_URLS.destinations = env.VITE_DESTINATIONS_API_URL ?? '/destinations/v1';

API_URLS.explorerUtils = '/explorer-utils';

API_URLS.features = setGraneroTarget(env.VITE_GRANERO_TARGET ?? 'live');

API_URLS.orders = env.VITE_ORDERS_API_URL ?? '/compute/ops/orders/v2';

API_URLS.pdaAdmin = env.VITE_PDA_ADMIN_URL ?? '/account/v1';

API_URLS.sceneTiles =
  env.VITE_SCENE_TILES_BASE_URL ?? 'https://tiles.planet.com/data/v1';

API_URLS.sceneTilesDomainTemplate =
  env.VITE_SCENE_TILES_DOMAIN_TEMPLATE ??
  'https://tiles{0-3}.planet.com/data/v1';

API_URLS.subscriptions = env.VITE_SUBSCRIPTIONS_API_URL ?? '/subscriptions/v1';

// Region-based APIs ------------------------------------------------------

const euc = API_URLS.regions[AWS_EU_CENTRAL_1];

API_URLS.accessControl = `${euc}/api/v1/acl`;

API_URLS.configuration = `${euc}/api/v2/configuration`;

API_URLS.catalog = `${euc}/catalog/v1`;

API_URLS.dataCollections =
  env.VITE_DATA_COLLECTIONS_URL ?? `${euc}/byoc/v1/collections`;

API_URLS.globalDataCollections = `${euc}/byoc/v1/global`;

API_URLS.ims = env.VITE_IMS_URL ?? `${euc}/account/v2`;

API_URLS.process = `${euc}/process/v1`;

API_URLS.shServiceLocations = `${euc}/api/v1/metadata/location`;

// the end ----------------------------------------------------------------

export default API_URLS;
